import React from 'react';
import { Flag } from 'lucide-react';
import { FLAG_CONFIG, FLAG_OPTIONS, FLAG_OPTIONS_WITH_GRAY, getFlagClasses } from '../hooks/useStatusFlag';

/**
 * Reusable status flag button group for green/yellow/red status tracking.
 * Replaces 5+ duplicate implementations across Mission, Objective, Activity, Task cards.
 * 
 * Usage:
 * <StatusFlagButtons
 *   currentFlag={item.status_flag}
 *   onChange={(flag) => handleFlagChange(item.id, flag)}
 *   includeGray={false}
 *   size="sm"
 * />
 */

export default function StatusFlagButtons({
  currentFlag,
  value, // alias for currentFlag
  onChange,
  includeGray = false,
  size = 'sm',
  variant = 'default', // 'default' or 'large' (card-style buttons)
  disabled = false,
  className = '',
  vertical = false,
  showLabels = false
}) {
  const flags = includeGray ? FLAG_OPTIONS_WITH_GRAY : FLAG_OPTIONS;
  const activeFlag = currentFlag || value;
  
  const sizeClasses = {
    xs: 'p-0.5',
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };
  
  const iconSizes = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Large card-style variant (used in ProgressReportModal)
  if (variant === 'large') {
    return (
      <div className={`flex gap-4 ${className}`}>
        {flags.map(flag => {
          const isActive = activeFlag === flag;
          const config = FLAG_CONFIG[flag];
          
          const bgClasses = {
            green: isActive ? 'bg-emerald-950/30 border-emerald-500 text-emerald-400' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800',
            yellow: isActive ? 'bg-amber-950/30 border-amber-500 text-amber-400' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800',
            red: isActive ? 'bg-rose-950/30 border-rose-500 text-rose-400' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800',
            gray: isActive ? 'bg-slate-800/50 border-slate-500 text-slate-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'
          };
          
          const dotClasses = {
            green: isActive ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-600',
            yellow: isActive ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]' : 'bg-slate-600',
            red: isActive ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]' : 'bg-slate-600',
            gray: isActive ? 'bg-slate-500' : 'bg-slate-600'
          };
          
          return (
            <div
              key={flag}
              onClick={() => !disabled && onChange(flag)}
              className={`flex-1 p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-center gap-2 ${bgClasses[flag]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className={`w-3 h-3 rounded-full ${dotClasses[flag]}`} />
              <span className="font-bold text-sm">{config.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Compact variant (vertical flags in minimal space)
  if (variant === 'compact') {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        {flags.map(flag => {
          const isActive = activeFlag === flag;
          const config = FLAG_CONFIG[flag];
          
          return (
            <button
              key={flag}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onChange(flag);
              }}
              disabled={disabled}
              className={`${sizeClasses[size]} rounded transition-colors ${getFlagClasses(flag, isActive)} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={config.label}
            >
              <Flag className={`${iconSizes[size]} text-white`} />
            </button>
          );
        })}
      </div>
    );
  }

  // Default icon-button variant
  return (
    <div className={`flex ${vertical ? 'flex-col' : ''} gap-1 ${className}`}>
      {flags.map(flag => {
        const isActive = activeFlag === flag;
        const config = FLAG_CONFIG[flag];
        
        return (
          <button
            key={flag}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onChange(flag);
            }}
            disabled={disabled}
            className={`${sizeClasses[size]} rounded transition-colors ${getFlagClasses(flag, isActive)} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={config.label}
          >
            <Flag className={`${iconSizes[size]} text-white`} />
            {showLabels && (
              <span className="ml-1 text-xs text-white">{config.shortLabel}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact badge version for display only
 */
export function StatusFlagBadge({ flag, size = 'sm', className = '' }) {
  const config = FLAG_CONFIG[flag] || FLAG_CONFIG.gray;
  
  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1'
  };

  return (
    <span className={`inline-flex items-center rounded ${config.badgeClass} ${sizeClasses[size]} ${className}`}>
      {config.label}
    </span>
  );
}

/**
 * Inline status indicator (just the colored dot)
 */
export function StatusFlagDot({ flag, size = 'sm', className = '' }) {
  const config = FLAG_CONFIG[flag] || FLAG_CONFIG.gray;
  
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4'
  };

  return (
    <span 
      className={`inline-block rounded-full ${config.activeClass} ${sizeClasses[size]} ${className}`}
      title={config.label}
    />
  );
}